import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useFeatures } from 'utils/util/features';
import { useCurrentOrganization } from 'utils/util/hooks';
import ConfirmAPI from '../../../utils/api/ConfirmAPI';
import { OnFilterChange } from '../../../utils/models/Objective';
import ReactTagsInput from '../../Widgets/Inputs/ReactTagsInput';

type TagOption = { name: string };

type Props = {
  disabled: boolean;
  value: string[];
  onChange: (delta: OnFilterChange) => void;
};

const ObjectivesOverviewTagFilter: FC<Props> = ({
  disabled,
  value,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const { isOptionEnabled } = useFeatures();
  const tagsEnabled = isOptionEnabled('objectives', 'tags_enabled');
  const currentOrganization = useCurrentOrganization();
  const [suggestions, setSuggestions] = useState<TagOption[]>([]);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    ConfirmAPI.sendRequestToConfirm(
      'GET',
      'tags',
      { organization: currentOrganization.id },
      (data: TagOption[] | null) => {
        if (data) {
          setSuggestions(data);
        }
      }
    );
  }, [currentOrganization?.id]);

  const tagObjects = useMemo(() => value.map((name) => ({ name })), [value]);

  const handleCallback = useCallback(
    (tags: TagOption[]) => {
      onChange({ tagNames: tags.map((t) => t.name) });
    },
    [onChange]
  );

  if (!tagsEnabled) return null;

  return (
    <ReactTagsInput
      disabled={disabled}
      className="w-100"
      value={tagObjects}
      suggestions={suggestions}
      allowNew={false}
      disableLengthCheck={true}
      minQueryLength={1}
      placeholder={formatMessage({
        id: 'app.views.objectives.objectives_overview_page.objectives_overview_tag_filter.placeholder',
        defaultMessage: 'Filter by tags',
      })}
      callback={handleCallback}
    />
  );
};

export default ObjectivesOverviewTagFilter;
